const en = {
  seo: {
    title: "Scan QR Code from an Image Online Free | QRCode GEN",
    description:
      "Upload a screenshot or image to scan a QR Code online in your browser. Read the result, open valid URLs, and learn how to scan on iPhone and Android.",
    keywords:
      "scan QR code from image, QR code scanner online, read QR code from screenshot, scan QR code on computer, iPhone QR scanner, Android QR scanner"
  },
  ui: {
    home: "Home",
    highlights: "Highlights",
    idleTitle: "Your result will appear here",
    idleDescription: "Choose an image to start reading its QR Code.",
    errorTitle: "The QR Code could not be read",
    backToScanner: "Back to the scanner ↑",
    faqEyebrow: "FAQ"
  },
  breadcrumb: "Scan a QR Code online",
  eyebrow: "QR Code reader for images",
  title: "Scan a QR Code from an image online",
  intro:
    "Scan a QR code from an image or camera. Results appear in a popup. Copy the content or select Open link for a valid HTTP/HTTPS URL.",
  benefits: [
    "No scanner app to install",
    "The image stays on your device",
    "QR Code content"
  ],
  tool: {
    title: "Upload an image containing a QR Code",
    description:
      "Use the original file or a clear screenshot that includes all four sides of the code and the white space around it.",
    dropTitle: "Drop your QR Code image here",
    dropActiveTitle: "Release the image to scan it",
    dropDescription: "or choose an image from your phone or computer",
    chooseFile: "Choose image",
    fileHint: "PNG, JPG, JPEG or WebP · up to 12 MB",
    privacy:
      "The image is processed in this browser and is not uploaded to QRCode GEN servers.",
    processing: "Reading the QR Code…",
    resultTitle: "QR Code content",
    resultType: "Content type",
    copy: "Copy",
    copied: "Copied",
    openLink: "Open link",
    scanAnother: "Scan another image",
    previewAlt: "Selected image containing a QR Code",
    idleTabTitle: "Reading QR Code…",
    idleTabMessage: "Reading the QR Code content…",
    noResult:
      "No QR Code was found. Try a sharper image that shows the complete code and its surrounding margin.",
    invalidType: "Choose a PNG, JPG, JPEG or WebP image.",
    tooLarge: "This image is larger than 12 MB. Reduce its file size and try again.",
    imageError: "This image could not be opened. Save it as PNG or JPG and try again.",
    copyError: "Automatic copying failed. Select and copy the result below.",
    safety:
      "Check the domain before entering a password, one-time code or payment details because QRCode GEN does not verify the destination site.",
    types: {
      url: "Website link",
      wifi: "Wi-Fi details",
      email: "Email",
      phone: "Phone number",
      sms: "SMS message",
      vcard: "vCard contact",
      payment: "Payment data",
      text: "Text"
    }
  },
  onlineGuide: {
    eyebrow: "Scan directly with QRCode GEN",
    title: "How to scan a QR Code from an image",
    intro:
      "This method is useful when the code is inside a screenshot, email, PDF or another screen on the device you are using.",
    visual: {
      alt: "Three steps showing a QR Code image being read in the browser",
      source: "QR image",
      process: "Read in browser",
      result: "View result"
    },
    steps: [
      {
        number: "01",
        title: "Start with a complete, sharp image",
        description:
          "Keep all three large finder squares and the clear margin around the QR Code. Avoid a blurred or heavily compressed screenshot."
      },
      {
        number: "02",
        title: "Drop or choose the file",
        description:
          "Add the image to the scanner above. It is decoded in the browser, so no camera or separate scanner app is required."
      },
      {
        number: "03",
        title: "Review the decoded content",
        description:
          "Results appear in a popup. Copy the content or select Open link for a valid HTTP/HTTPS URL."
      },
      {
        number: "04",
        title: "Copy the result or scan another image",
        description:
          "Copy the content to use elsewhere, or check the address and select Open link. Close the result window to return to the scanner."
      }
    ]
  },
  deviceGuides: [
    {
      id: "scan-qr-code-iphone",
      icon: "mobile",
      eyebrow: "iOS · iPhone and iPad",
      title: "How to scan a QR Code on iPhone",
      description:
        "The Camera app and Code Scanner in Control Center can read QR Codes without a separate app.",
      steps: [
        "Open Camera from the Home Screen, Lock Screen or Control Center.",
        "In Photo mode, hold the device so the whole QR Code sits inside the frame.",
        "Wait for the link or notification to appear, then tap it to open the content.",
        "If Code Scanner is missing, add Scan Code to Control Center and use it from there."
      ],
      note:
        "When the QR Code is saved on the same iPhone, upload the image above instead of finding a second device.",
      visual: {
        type: "iphone",
        alt: "iPhone Camera recognizing a QR Code and showing a link",
        app: "Camera",
        status: "Link detected",
        caption: "Keep the complete QR Code in frame, then tap the link that appears."
      }
    },
    {
      id: "scan-qr-code-android",
      icon: "globe",
      eyebrow: "Android",
      title: "How to scan a QR Code on Android",
      description:
        "The available scanner depends on the phone maker, Android version and camera app.",
      steps: [
        "Open Camera in Photo mode and point it at the QR Code. Tap the banner if one appears.",
        "If Camera does not recognize it, open Google Lens from Camera, Google Photos or the Google app.",
        "On Samsung Galaxy, try Camera or Scan QR code in Quick Settings; some models require the camera setting to be enabled.",
        "For an image already saved on the phone, open it in Gallery or Google Photos and use Lens, or upload it here."
      ],
      note:
        "Camera from Google supports QR scanning only on selected devices. Button names and locations vary between manufacturers.",
      visual: {
        type: "android",
        alt: "Android phone using Google Lens to scan a QR Code",
        app: "Google Lens",
        status: "Tap to open",
        caption: "If the standard Camera cannot read the code, try Lens or the phone's built-in QR scanner."
      }
    },
    {
      id: "scan-qr-code-social-apps",
      icon: "message",
      eyebrow: "Messaging, social and payment apps",
      title: "Scan with LINE and other in-app scanners",
      description:
        "Some apps include their own scanner, but each one may recognize only the QR formats it supports.",
      steps: [
        "LINE can scan LINE account codes, web links, text and several other common QR formats.",
        "In LINE's scanner, choose the image icon to read a QR Code from a saved picture.",
        "Social apps may prioritize profile, login or sharing codes from their own platform.",
        "Use a compatible bank or wallet app for payment QR Codes so it can show the recipient and amount before approval."
      ],
      note:
        "An in-app scanner is not always a general QR reader. If it rejects the code, use Camera, Lens or the upload tool above.",
      visual: {
        type: "social",
        alt: "QR scanners in LINE, social apps and banking apps",
        app: "In-app scanner",
        status: "Use the app that matches the code",
        labels: ["LINE", "Social", "Banking"],
        caption: "Each app may support only selected QR Code types."
      }
    },
    {
      id: "other-qr-code-scanners",
      icon: "qrcode",
      eyebrow: "Other ways to scan",
      title: "What to try when Camera cannot read the QR Code",
      description:
        "Try the built-in tools first instead of immediately installing an unfamiliar scanner app.",
      steps: [
        "Show the QR Code on one screen and scan it with the Camera on a second phone.",
        "Save or capture the image, then upload it to QRCode GEN from a computer or phone.",
        "Use Google Lens or the QR recognition option already available in the photo gallery.",
        "If another app is necessary, install it only from an official store and review the permissions it requests."
      ],
      note:
        "A retail barcode scanner must support 2D codes. Hardware designed only for 1D barcodes cannot read QR Codes.",
      visual: {
        type: "alternatives",
        alt: "A saved QR Code image being decoded on a computer",
        app: "Saved image",
        status: "QR Code content",
        caption: "Save a clear image or screenshot, then upload it to the scanner above."
      }
    }
  ],
  troubleshooting: {
    eyebrow: "Image not recognized?",
    title: "Common reasons a QR Code image will not scan",
    items: [
      {
        title: "The code or its clear margin is cropped",
        description:
          "Use the complete image. The three large finder squares and the light margin around the code must remain visible."
      },
      {
        title: "The QR Code is tiny inside a large screenshot",
        description:
          "Crop away unrelated content without cutting into the code, then save the image at a high quality."
      },
      {
        title: "The image is blurred, compressed or reflective",
        description:
          "Use the original file, take a closer photo or increase screen brightness when photographing another device."
      },
      {
        title: "The pattern is too dense for its printed size",
        description:
          "Long URLs, detailed vCards and large text blocks produce denser codes. Shorten the content, use a short link or create a dynamic QR Code."
      },
      {
        title: "The image contains several QR Codes",
        description:
          "Crop around the code you need. The scanner currently returns the first QR Code it recognizes."
      },
      {
        title: "The link was decoded but the destination will not load",
        description:
          "The QR image may still be valid. Check the connection, file permissions, URL spelling and whether the destination still exists."
      }
    ]
  },
  safety: {
    eyebrow: "Check before you trust",
    title: "A readable QR Code is not necessarily a safe one",
    description:
      "Check the domain before entering a password, one-time code or payment details because QRCode GEN does not verify the destination site.",
    checks: [
      "Read the full domain and watch for characters that imitate a known brand",
      "Do not enter a password, one-time code or card details until the site is verified",
      "Do not install files from outside an official app store",
      "For payments, confirm the recipient, amount and currency in the banking app"
    ]
  },
  faq: {
    eyebrow: "FAQ",
    title: "Frequently asked questions about scanning QR Codes online",
    items: [
      {
        q: "Is my QR Code image uploaded to a server?",
        a: "No. The image pixels are processed in the browser on your device. QRCode GEN does not need to upload the file to decode it."
      },
      {
        q: "Can I scan a QR Code from a screenshot?",
        a: "Yes, if the screenshot is sharp and contains the complete code. Crop closer when the code is very small, but keep its edges and surrounding margin."
      },
      {
        q: "Which image formats are supported?",
        a: "PNG, JPG, JPEG and WebP files up to 12 MB are supported. Convert HEIC images to JPG or PNG before uploading."
      },
      {
        q: "Why can a clear-looking image still fail?",
        a: "The clear margin may be missing, the code may be distorted or low contrast, or too much data may be packed into a small image. Try the original file or a higher-resolution crop."
      },
      {
        q: "Can this tool process a payment QR Code?",
        a: "It may display the encoded data, but it does not verify the recipient or make a payment. Scan again with a compatible bank app and confirm every payment field there."
      },
      {
        q: "Why did the URL decode but not open?",
        a: "Select Open link in the result popup to visit the address. If it still does not open, check your connection and any sign-in or file permissions required by the destination."
      }
    ]
  },
  relatedTitle: "Related resources",
  relatedLinks: [
    { href: "/help", label: "Fix a QR Code that Camera cannot recognize" },
    { href: "/products/short-link", label: "Shorten a long URL for easier scanning" },
    { href: "/products/custom-qr-code", label: "Customize a QR Code without losing readability" },
    { href: "/what-is-qr-code", label: "How does a QR Code work?" }
  ],
  cta: {
    title: "Need to create a new QR Code?",
    description:
      "Choose the content type, check the data, then generate and download the QR Code in your browser.",
    href: "/",
    label: "Create a QR Code free"
  }
};

export default en;
